import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { Package, Plus, Clock, DollarSign, Pencil, Trash2, Link2, Users, Search } from 'lucide-react';
import { Badge } from '../ui/badge';

const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
const emptyForm = { name: '', hours: '', price: '', validity_months: '', description: '' };

export default function PackagesManagement() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('packages');
  const [packageCustomers, setPackageCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchPackages(); }, []);

  useEffect(() => {
    if (activeTab === 'customers') fetchPackageCustomers();
  }, [activeTab]);

  const fetchPackages = async () => {
    try {
      const response = await axiosInstance.get('/packages');
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await axiosInstance.get('/packages/customers');
      setPackageCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load package customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPkg(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (pkg) => {
    setEditingPkg(pkg);
    setFormData({
      name: pkg.name,
      hours: String(pkg.hours),
      price: String(pkg.price),
      validity_months: String(pkg.validity_months),
      description: pkg.description || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      hours: parseInt(formData.hours),
      price: parseFloat(formData.price),
      validity_months: parseInt(formData.validity_months)
    };
    try {
      if (editingPkg) {
        await axiosInstance.put(`/packages/${editingPkg.id}`, payload);
        toast.success('Package updated successfully');
      } else {
        await axiosInstance.post('/packages', payload);
        toast.success('Package created successfully');
      }
      setShowDialog(false);
      setFormData(emptyForm);
      setEditingPkg(null);
      fetchPackages();
    } catch (error) {
      toast.error(editingPkg ? 'Failed to update package' : 'Failed to create package');
    }
  };

  const handleDelete = async (pkg) => {
    try {
      await axiosInstance.delete(`/packages/${pkg.id}`);
      toast.success('Package deleted');
      setDeleteConfirm(null);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const copyPurchaseLink = (pkg) => {
    const link = `${FRONTEND_URL}/purchase/package/${pkg.id}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success(`Purchase link copied for "${pkg.name}"`);
    }).catch(() => {
      // Fallback for non-https
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(`Purchase link copied for "${pkg.name}"`);
    });
  };

  const filteredCustomers = packageCustomers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  return (
    <div data-testid="packages-management">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Packages Management
        </h2>
        <p className="text-gray-600">Create and manage service packages</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'packages' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          onClick={() => setActiveTab('packages')}
          data-testid="tab-packages"
        >
          <Package size={16} className="inline mr-2" />
          Packages
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'customers' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          onClick={() => setActiveTab('customers')}
          data-testid="tab-package-customers"
        >
          <Users size={16} className="inline mr-2" />
          Package Customers
        </button>
      </div>

      {activeTab === 'packages' && (
        <>
          <div className="mb-6">
            <Button onClick={openCreate} data-testid="add-package-btn">
              <Plus size={16} className="mr-2" />
              Create Package
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className="backdrop-blur-sm bg-gradient-to-br from-white to-teal-50/30 shadow-lg border-0 hover:shadow-xl transition-all"
                  data-testid={`package-card-${pkg.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Package className="text-[#595959]" size={32} />
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => copyPurchaseLink(pkg)}
                          title="Copy Purchase Link"
                          data-testid={`copy-link-${pkg.id}`}
                        >
                          <Link2 size={16} className="text-blue-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(pkg)} data-testid={`edit-pkg-${pkg.id}`}>
                          <Pencil size={16} className="text-gray-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDeleteConfirm(pkg)} data-testid={`delete-pkg-${pkg.id}`}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                      <div className="text-3xl font-bold text-gray-900">${pkg.price}</div>
                    </div>
                    <CardDescription>{pkg.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock size={18} className="text-[#595959]" />
                      <span className="font-medium">{pkg.hours} Hours</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <DollarSign size={18} className="text-[#595959]" />
                      <span className="font-medium">Valid for {pkg.validity_months} months</span>
                    </div>
                    <div className="pt-3 border-t">
                      <p className="text-sm text-gray-500">Rate: ${(pkg.price / pkg.hours).toFixed(2)}/hour</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && packages.length === 0 && (
            <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No packages available</p>
                <p className="text-gray-400 text-sm">Create your first package to get started</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === 'customers' && (
        <div data-testid="package-customers-section">
          {/* Search */}
          <div className="mb-4 relative max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-package-customers"
            />
          </div>

          {customersLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No package customers found</p>
                <p className="text-gray-400 text-sm">Customers who purchase packages will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-lg border-0 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="package-customers-table">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Package Hours</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Used Hours</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Remaining Hours</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Remaining Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.map((c) => (
                        <tr key={c.customer_id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`pkg-customer-row-${c.customer_id}`}>
                          <td className="py-3 px-4 font-medium text-gray-900">{c.name}</td>
                          <td className="py-3 px-4 text-gray-600">{c.email}</td>
                          <td className="py-3 px-4 text-gray-600">{c.phone}</td>
                          <td className="py-3 px-4 text-center font-medium">{c.total_hours}h</td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="outline" className="text-orange-600 border-orange-200">{c.used_hours}h</Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={c.remaining_hours > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {c.remaining_hours}h
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={c.remaining_days > 30 ? 'bg-blue-100 text-blue-700' : c.remaining_days > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                              {c.remaining_days} days
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPkg ? 'Edit Package' : 'Create New Package'}</DialogTitle>
            <DialogDescription>{editingPkg ? 'Update package details' : 'Define a new service package'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="package-name">Package Name</Label>
              <Input id="package-name" data-testid="package-name-field" placeholder="e.g., Basic Package" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hours">Hours</Label>
                <Input id="hours" type="number" data-testid="package-hours-field" placeholder="20" value={formData.hours} onChange={(e) => setFormData({ ...formData, hours: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price ($)</Label>
                <Input id="price" type="number" step="0.01" data-testid="package-price-field" placeholder="1000" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="validity">Validity (Months)</Label>
              <Input id="validity" type="number" data-testid="package-validity-field" placeholder="6" value={formData.validity_months} onChange={(e) => setFormData({ ...formData, validity_months: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" data-testid="package-description-field" placeholder="Package details..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
            <Button type="submit" className="w-full" data-testid="submit-package-btn">
              {editingPkg ? 'Update Package' : 'Create Package'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-pkg">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
